import {
    DiffOutlined,
    EyeOutlined, FileDoneOutlined, QrcodeOutlined, SearchOutlined, DeleteOutlined,
    StopOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import {
    Button, Checkbox, DatePicker, Modal, Space,
} from 'antd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import React, {
    useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import { FilterDropdownProps } from 'antd/es/table/interface';
import produce from 'immer';
import { useOrdersList } from '../hooks/useOrdersList';
import { IOrder } from '../types/order';
import Table from './table';
import OrderQrCodeModal, { IOrderQrCodeModalRef } from './order-qr-code-modal';
import { useOrdersListFilters } from '../hooks/useOrdersListFilters';
import SearchFilter from './Table/SearchFilter';
import { useFetch } from '../hooks/useFetch';
import { USER_ROLE_MODERATOR_LIMITED, useUser } from '../hooks/useUser';
import RusPostManifestFormModal, { IRusPostManifestFormModalRef } from './RusPost/manifest-form';
import GlsCreateOrderFormModal, { IGlsCreateOrderFormModalRef } from './GLS/create-order-form-modal';

export interface IOrdersListProps {
    search?: string;
    userId?: number;
    rowSelection?: boolean;
    onSelectedRowsChange?: (rows: IOrder[]) => void;
}

export interface IOrdersListRef {
    clearSelectedRows: () => void;
    update: () => void;
}

const OrdersList = React.forwardRef(({
    search, userId, rowSelection, onSelectedRowsChange,
}: IOrdersListProps, ref: any) => {
    const fd = useFetch();
    const notifyServicesFetch = useFetch();
    const {
        data, filters, loading, onTableChange, update, setOrderAsPaid,
    } = useOrdersList({ search, userId });
    const navigate = useNavigate();
    const { t } = useTranslation();
    const qrCodeModal = useRef<IOrderQrCodeModalRef>();
    const listFilters = useOrdersListFilters();
    const notifyServicesOrder = useRef<IOrder>();
    const [selectedRows, setSelectedRows] = useState<IOrder[]>([]);
    const user = useUser({ canFetch: false, canLogoutBy401: false, showFetchMessages: false });
    const rusPostManifestForm = useRef<IRusPostManifestFormModalRef>();
    const glsCreateOrderForm = useRef<IGlsCreateOrderFormModalRef>();

    useEffect(() => {
        if (typeof onSelectedRowsChange === 'function') {
            onSelectedRowsChange(selectedRows);
        }
    }, [selectedRows]);

    useImperativeHandle(ref, () => ({
        clearSelectedRows: () => setSelectedRows([]),
        update,
    }));

    const createGlsOrder = (row: IOrder) => {
        glsCreateOrderForm.current?.show(row);
    };

    const onGetQrCodeClick = (row: IOrder) => qrCodeModal.current?.show(row);

    const notifyServices = async (row: IOrder) => {
        notifyServicesOrder.current = row;
        await notifyServicesFetch.request({
            url: `/orders/${row.id}/notify-third-party-services`,
            method: 'get',
        });
        update();
    };

    const sendRusPostManifest = (row: IOrder) => {
        rusPostManifestForm.current?.show([row]);
    };

    const onOrderCancelClick = async (row: IOrder) => {
        Modal.confirm({
            title: t('confirmAction'),
            content: t('orderCancelConfirmation'),
            onOk: async () => {
                await fd.request({
                    url: `/orders/${row.id}/cancel`,
                    method: 'get',
                });
                update();
            },
            okText: t('confirm'),
        });
    };

    const onOrderDeleteClick = async (row: IOrder) => {
        Modal.confirm({
            title: t('confirmAction'),
            content: t('deleteConfirmation'),
            onOk: async () => {
                await fd.request({
                    url: `/orders/${row.id}/delete`,
                    method: 'get',
                });
                update();
            },
            okText: t('confirm'),
        });
    };

    const onRowSelect = (row: IOrder) => {
        setSelectedRows(produce(selectedRows, (draft) => {
            const index = draft.findIndex((obj) => obj.id === row.id);
            if (index > -1) {
                draft.splice(index, 1);
            } else {
                draft.push(row);
            }
        }));
    };

    const actions = (_: any, record: IOrder) => (
        <Space>
            <Button
                icon={<EyeOutlined />}
                onClick={() => navigate(`/order/${record.id}`)}
                size="small"
            />
            <Button
                disabled={!record.qr_code_available}
                type="primary"
                icon={<QrcodeOutlined />}
                title={t('getQrCode') as string}
                onClick={() => onGetQrCodeClick(record)}
                size="small"
            />
            {user.info?.role_value !== USER_ROLE_MODERATOR_LIMITED && (
                <>
                    {!record.paid && (
                        <Button
                            type="primary"
                            icon={<FileDoneOutlined />}
                            title={t('makeOrderPaid') as string}
                            onClick={() => setOrderAsPaid(record)}
                            size="small"
                        />
                    )}
                    {record.paid_data_updated && (
                        <Button
                            icon={<DiffOutlined />}
                            onClick={() => notifyServices(record)}
                            size="small"
                            title={t('paidDataUpdated') as string}
                            loading={notifyServicesFetch.loading && notifyServicesOrder.current?.id === record.id}
                        />
                    )}
                    {record.ruspost_manifest_not_sent && (
                        <Button
                            icon={<DiffOutlined />}
                            onClick={() => sendRusPostManifest(record)}
                            size="small"
                            title={t('sendRusPostManifest') as string}
                        />
                    )}
                    {record.gls_error && (
                        <Button
                            onClick={() => createGlsOrder(record)}
                            size="small"
                            title={t('createGlsOrder') as string}
                            danger
                            type="primary"
                        >
                            GLS
                        </Button>
                    )}
                    {record.paid && (
                        <Button
                            icon={<StopOutlined />}
                            size="small"
                            title={t('cancel') as string}
                            onClick={() => onOrderCancelClick(record)}
                            danger
                        />
                    )}
                    {!record.paid && (
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            title={t('delete') as string}
                            onClick={() => onOrderDeleteClick(record)}
                            danger
                        />
                    )}
                </>
            )}
        </Space>
    );

    const dateFilter = ({
        clearFilters, selectedKeys, setSelectedKeys, confirm,
    }: FilterDropdownProps) => (
        <div className="ant-table-filter-dropdown">
            <div className="ant-dropdown-menu">
                <div className="ant-dropdown-menu-item">
                    <DatePicker.RangePicker format="DD.MM.YYYY" value={selectedKeys as any} onChange={setSelectedKeys as any} />
                </div>
            </div>
            <div className="ant-table-filter-dropdown-btns">
                <Button type="default" size="small" onClick={clearFilters}>
                    Сбросить
                </Button>
                <Button type="primary" size="small" onClick={() => confirm()}>
                    Применить
                </Button>
            </div>
        </div>
    );

    return (
        <>
            <OrderQrCodeModal ref={qrCodeModal} />
            <RusPostManifestFormModal ref={rusPostManifestForm} onHide={update} />
            <GlsCreateOrderFormModal ref={glsCreateOrderForm} onHide={update} />
            {selectedRows.length > 0 && (
                <Space style={{ flexWrap: 'wrap', marginBottom: 20 }}>
                    <span>Выбранные записи: </span>
                    {selectedRows.map((obj) => (
                        <Button size="small" onClick={() => onRowSelect(obj)}>
                            <span key={obj.id}>{obj.id}</span>
                            <CloseOutlined />
                        </Button>
                    ))}
                </Space>
            )}
            <Table
                loading={loading}
                scroll={{ x: 2000 }}
                dataSource={data?.list}
                columns={[
                    {
                        dataIndex: 'id',
                        title: '№',
                        align: 'center',
                        width: 100,
                        filterDropdown: SearchFilter,
                        filterIcon: <SearchOutlined />,
                    },
                    {
                        dataIndex: 'creator.name',
                        title: t('client'),
                        align: 'center',
                        filterDropdown: SearchFilter,
                        filterIcon: <SearchOutlined />,
                        render: (val, record) => (
                            <Link to={`/client/${record.creator_id}`}>
                                {val}
                            </Link>
                        ),
                        width: 180,
                    },
                    {
                        dataIndex: 'tracking',
                        title: t('trackingNumber'),
                        align: 'center',
                        width: 140,
                    },
                    {
                        dataIndex: 'glsTrackingCode.tracking_id',
                        title: `${t('trackingNumber')} (GLS)`,
                        align: 'center',
                        filterDropdown: SearchFilter,
                        filterIcon: <SearchOutlined />,
                        width: 180,
                    },
                    {
                        dataIndex: 'created_at',
                        title: t('orderCreatedDate'),
                        render: (_, row: IOrder) => moment(row.created_at).format('DD.MM.YYYY').toString(),
                        filterDropdown: dateFilter,
                        align: 'center',
                        filtered: !!filters?.find((el) => el.column === 'created_at'),
                        filteredValue: filters?.find((el) => el.column === 'created_at')?.values,
                        width: 140,
                    },
                    {
                        dataIndex: 'from_to',
                        title: t('direction'),
                        align: 'center',
                        width: 160,
                    },
                    {
                        dataIndex: 'sender.name',
                        title: t('fromWhom'),
                        align: 'center',
                        filterDropdown: SearchFilter,
                        filterIcon: <SearchOutlined />,
                        width: 180,
                    },
                    {
                        dataIndex: 'recipient.name',
                        title: t('toWhom'),
                        align: 'center',
                        filterDropdown: SearchFilter,
                        filterIcon: <SearchOutlined />,
                        width: 180,
                    },
                    {
                        dataIndex: 'status',
                        title: t('status'),
                        align: 'center',
                        render: (_, row: IOrder) => row.status.text,
                        width: 140,
                    },
                    {
                        dataIndex: 'send_datetime',
                        title: t('sendTime'),
                        align: 'center',
                        width: 180,
                    },
                    {
                        dataIndex: 'delivery_date',
                        title: t('deliveryDate'),
                        align: 'center',
                        width: 180,
                    },
                    {
                        dataIndex: 'total_weight',
                        title: t('orderPackageWeight'),
                        align: 'center',
                        width: 100,
                    },
                    {
                        dataIndex: 'type',
                        title: t('orderType'),
                        filters: listFilters?.orderTypes,
                        align: 'center',
                        filtered: !!filters?.find((el) => el.column === 'type'),
                        filteredValue: filters?.find((el) => el.column === 'type')?.values,
                        width: 200,
                    },
                    {
                        dataIndex: 'deliveryTypeName',
                        title: t('deliveryType'),
                        filters: listFilters?.deliveryTypes,
                        align: 'center',
                        filtered: !!filters?.find((el) => el.column === 'deliveryTypeName'),
                        filteredValue: filters?.find((el) => el.column === 'deliveryTypeName')?.values,
                        width: 120,
                    },
                    {
                        dataIndex: 'paid',
                        title: t('paid'),
                        render: (_, row: IOrder) => (row.paid ? t('yes') : t('no')),
                        filters: [{
                            text: t('yes'),
                            value: true,
                        }, {
                            text: t('no'),
                            value: false,
                        }],
                        align: 'center',
                        filtered: !!filters?.find((el) => el.column === 'paid'),
                        filteredValue: filters?.find((el) => el.column === 'paid')?.values,
                        width: 120,
                    },
                    {
                        title: '',
                        key: 'action',
                        render: actions,
                        fixed: 'right',
                        align: 'center',
                        width: 200,
                    },
                ]}
                pagination={{
                    current: data?.currentPage,
                    total: data?.total,
                    pageSize: data?.perPage,
                    showSizeChanger: false,
                }}
                onChange={onTableChange}
                rowSelection={rowSelection ? {
                    renderCell: (_, record) => (
                        <Checkbox
                            checked={selectedRows.findIndex((obj) => obj.id === record.id) > -1}
                            // disabled={!record.paid_data_updated}
                            onChange={() => onRowSelect(record)}
                        />
                    ),
                } : undefined}
                onRow={(record) => {
                    const classes = [];
                    if (record.parcel_pickup_expires || record.gls_error) classes.push('expiring-soon-row');
                    if (record.is_return) classes.push('return-order');
                    if (record.ruspost_manifest_not_sent) classes.push('ruspost_manifest_not_sent');
                    return {
                        className: classes.join(' '),
                    };
                }}
            />
        </>
    );
});

OrdersList.defaultProps = {
    userId: undefined,
};

export default OrdersList;
